import React, {useState, useEffect, useMemo} from 'react';
import {object, array, number, func} from 'prop-types';
import getAppliedFormatting from "../../util/getAppliedFormatting";
import {useEditRow} from "../../util/useEditRow";
import Error from "../Error";

const Table = props => {
    const {
        headers,
        columnTypes,
        tableData,
        setCurrentData: propsSetCurrentData,
        limit,
        searchVal,
        displayTotal,
        endpoint,
        setError,
        visibleColumns
    } = props;

    const url = new URL(window.location.href);
    const [curPage, setCurPage] = useState(parseInt(url.searchParams.get("page")) || 0);
    const numPages = Math.ceil(tableData.length / limit);
    const dataStartPoint = curPage * limit;
    const [sortDirection, setSortDirection] = useState('asc');
    const [currentData, setCurrentData] = useState(tableData);
    const editRowFn = useEditRow({
        endpoint,
        setError
    });

    const getPagesArray = () => {
        let array = [];
        for (let i = curPage; i < numPages; i++) {
            array.push(i);
        }
        return array;
    }

    const [pageNumArray, setPageNumArray] = useState(getPagesArray);

    useEffect(() => {
        if (url.searchParams.get("search") !== searchVal) {
            setCurPage(0);
        }
    }, [searchVal]);

    useEffect(() => {
        url.searchParams.set("page", curPage);
        history.pushState({}, "Bridge Search: " + url.searchParams.toString(), url.toString());
    }, [curPage]);

    const getPagerButtons = useMemo(() => {
        const buttonHTML = [];

        if (currentData.length < limit) {
            return buttonHTML;
        }

        if (curPage !== 0) {
            buttonHTML.push(
                (<button type={"button"} key={`first[${curPage}]`} className={"pager pager__icon"}
                         onClick={() => setCurPage(0)}>
                    <img src="/images/first.png" alt="first page button"/>
                </button>)
            )
        }

        if (curPage > 0) {
            buttonHTML.push(
                (<button type={"button"} key={`previous[${curPage}]`} className={"pager pager__icon"}
                         onClick={() => setCurPage(curPage - 1)}>
                    <img src="/images/prev.png" alt="previous page button"/>
                </button>)
            )
        }

        buttonHTML.push(<p key={'pageCount'} className={"blue italic"}>Page {curPage + 1} of {numPages}</p>)

        if (curPage < numPages - 1) {
            buttonHTML.push(
                (<button type={"button"} key={`next[${curPage}]`} className={"pager pager__icon"}
                         onClick={() => setCurPage(curPage + 1)}>
                    <img src="/images/next.png" alt="next page button"/>
                </button>)
            )
        }

        if (curPage < numPages - 1) {
            buttonHTML.push(
                (<button type={"button"} key={`last[${curPage}]`} className={"pager pager__icon"}
                         onClick={() => setCurPage(numPages - 1)}>
                    <img src="/images/last.png" alt="last page button"/>
                </button>)
            )
        }


        return (
            <div className={"form__row form__row--justify-right form__row--align-center no-print"}>
                {buttonHTML}
            </div>
        );
    }, [curPage, currentData, limit, numPages]);

    const tableHeaderAuxColumns = Object.keys(props.auxColumns).map((field, key) => {
        return (
            <th className={`table__headers ${props.auxColumns[field].name.includes('Delete') ? 'table__headers-delete' : ''} no-print`} key={key}>
                {props.auxColumns[field].name.includes('Delete') ?
                    <img src="/images/delete_trashcan_white.png" alt="trashcan" className={"table__headers-delete-icon"}/> :
                    null
                }
            </th>
        );
    });

    const tableHeaders = Object.keys(headers).filter(column => {
        return !!visibleColumns.find(visCol => column === visCol);
    }).map(column => {
        return (
            <th className="table__headers" id={column} key={column} onClick={() => sortHeaders(column)}>
                {headers[column]}
            </th>
        );
    });

    const sortHeaders = key => {
        const newDataOrder = [...currentData];
        if (sortDirection === 'asc') {
            newDataOrder.sort((a, b) => {
                let dataA = a[key] !== null ? a[key] : '';
                let dataB = b[key] !== null ? b[key] : '';
                return (dataA < dataB) ? -1 : (dataA > dataB) ? 1 : 0;
            });
            setSortDirection('desc');
        } else {
            newDataOrder.sort((a, b) => {
                let dataA = a[key] !== null ? a[key] : '';
                let dataB = b[key] !== null ? b[key] : '';
                return (dataA < dataB) ? 1 : (dataA > dataB) ? -1 : 0;
            });
            setSortDirection('asc');
        }

        propsSetCurrentData ? propsSetCurrentData(newDataOrder) : '';
        setCurrentData(newDataOrder);
    };

    const getDataColumns = data => {
        const columns = Object.keys(headers).filter(field => {
            return visibleColumns.includes(field);
        }).map((field, indexColumn) => {
            return (<td key={'column' + indexColumn} onClick={() => {
                editRowFn({data, setCurrentData, currentData: tableData})
            }} className={"table__data"}>{getAppliedFormatting(data[field], columnTypes[field])}</td>);
        });
        const auxColumns = props.auxColumns.map((FieldReactComp, auxIndex) => {
            return (<FieldReactComp endpoint={props.endpoint} endpointAppend={props.endpointAppend}
                                    setError={props.setError} key={data.id + "_" + auxIndex} data={data}
                                    setCurrentData={propsSetCurrentData ? propsSetCurrentData : setCurrentData} tableData={currentData}/>);
        });
        return [...columns, ...auxColumns];
    };

    const tableRows = tableData.slice(dataStartPoint, dataStartPoint + limit).map((data) => {
        return (
            <tr key={data.id} className="table__row">
                {getDataColumns(data)}
            </tr>
        );
    });

    return (
        <>
            <table className="table print">
                <thead className="table__head" id="table-headers">
                <tr className="table__row">
                    {[...tableHeaders, ...tableHeaderAuxColumns]}
                </tr>
                </thead>
                <tbody id="table-data">
                {tableRows}
                </tbody>
            </table>
            {displayTotal ?
                (<h3 className="header3 header3--blue bold text-align-right">Total ${displayTotal}</h3>) : null}
            {getPagerButtons}
        </>
    );
}

Table.defaultProps = {
    auxColumns: [],
    tableData: [],
    limit: 10,
    buttonLimit: 3,
    displayTotal: null,
    visibleColumns: []
}

Table.propTypes = {
    auxColumns: array,
    tableData: array.isRequired,
    headers: object.isRequired,
    columnTypes: object.isRequired,
    limit: number,
    buttonLimit: number,
    setCurrentData: func,
    displayTotal: number,
    visibleColumns: array
}

export default Table;

//# sourceMappingURL=Table.js.map
